// Prise de rendez-vous : service (agenda public de la collectivité), semaine, créneau avec places
// restantes, objet facultatif, confirmation (POST /usager/rendez-vous). Heures de Libreville.
import { lundiDeLaSemaine } from '@cc/contrats';
import { Bandeau, Bouton, EtatVide, LienBouton, Selection, ZoneTexte } from '@cc/ui';
import { CalendarDays } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Navigate } from 'react-router';
import { requete } from '../../../api/client';
import { EtatRequete } from '../../../commun/EtatRequete';
import { dateHeure } from '../../../commun/format';
import { useAction } from '../../../commun/useAction';
import { CLES, useAgendas, useCreneaux } from '../api';
import { useCollectiviteChoisie, useCollectivitesPubliques } from '../collectivite';
import { Bloc, EnTeteCitoyen, ExigeConnexion } from '../commun';
import type { RendezVousUsager } from '../types';
import styles from '../citoyen.module.css';
import depot from '../depot/depot.module.css';

const FUSEAU = 'Africa/Libreville';
const jourLong = (iso: string) => new Date(iso).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: FUSEAU });
const heure = (iso: string) => new Date(iso).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: FUSEAU });

function decalerSemaine(lundi: string, semaines: number): string {
  const d = new Date(`${lundi}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + semaines * 7);
  return d.toISOString().slice(0, 10);
}

function Creneaux({ code, agendaId, choisi, onChoisir }: { code: string; agendaId: string; choisi: string | null; onChoisir: (debut: string) => void }) {
  const [semaine, setSemaine] = useState(() => lundiDeLaSemaine(new Date().toISOString().slice(0, 10)));
  const creneaux = useCreneaux(code, agendaId, semaine);
  const parJour = useMemo(() => {
    const m = new Map<string, { debut: string; fin: string; places_restantes: number }[]>();
    for (const c of creneaux.data?.creneaux ?? []) {
      const j = jourLong(c.debut);
      m.set(j, [...(m.get(j) ?? []), c]);
    }
    return [...m.entries()];
  }, [creneaux.data]);
  const aujourdhui = lundiDeLaSemaine(new Date().toISOString().slice(0, 10));
  return (
    <div className={styles.pile}>
      <div className={styles.ligneBoutons} role="group" aria-label="Semaine">
        <Bouton variante="secondaire" petit disabled={semaine <= aujourdhui} onClick={() => setSemaine(decalerSemaine(semaine, -1))}>
          Semaine précédente
        </Bouton>
        <span>Semaine du {new Date(`${semaine}T00:00:00Z`).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', timeZone: 'UTC' })}</span>
        <Bouton variante="secondaire" petit onClick={() => setSemaine(decalerSemaine(semaine, 1))}>
          Semaine suivante
        </Bouton>
      </div>
      <EtatRequete chargement={creneaux.isPending} erreur={creneaux.error} onReessayer={() => void creneaux.refetch()} fonction="Les créneaux" lignes={4}>
        {parJour.length === 0 ? (
          <EtatVide titre="Aucun créneau cette semaine" icone={CalendarDays}>
            <p>Essayez la semaine suivante.</p>
          </EtatVide>
        ) : (
          parJour.map(([jour, liste]) => (
            <fieldset key={jour} className={depot.groupe}>
              <legend>{jour}</legend>
              <div className={styles.filtres} role="radiogroup" aria-label={`Créneaux du ${jour}`}>
                {liste.map((c) => (
                  <button
                    key={c.debut}
                    type="button"
                    role="radio"
                    aria-checked={choisi === c.debut}
                    className={styles.filtre}
                    disabled={c.places_restantes === 0}
                    onClick={() => onChoisir(c.debut)}
                    aria-label={`${heure(c.debut)}, ${c.places_restantes === 0 ? 'complet' : `${c.places_restantes} place${c.places_restantes > 1 ? 's' : ''}`}`}
                  >
                    <span className={styles.mono}>{heure(c.debut)}</span>
                    {c.places_restantes === 0 ? ' complet' : ''}
                  </button>
                ))}
              </div>
            </fieldset>
          ))
        )}
      </EtatRequete>
    </div>
  );
}

function Prise({ code }: { code: string }) {
  const collectivites = useCollectivitesPubliques();
  const [collectivite, setCollectivite] = useState(code);
  const agendas = useAgendas(collectivite);
  const [agendaId, setAgendaId] = useState('');
  const [debut, setDebut] = useState<string | null>(null);
  const [objet, setObjet] = useState('');
  const [confirme, setConfirme] = useState<RendezVousUsager | null>(null);
  const reservation = useAction({
    executer: () => requete<RendezVousUsager>('/usager/rendez-vous', { methode: 'POST', corps: { collectivite, agenda_id: agendaId, debut, ...(objet.trim() ? { objet: objet.trim() } : {}) } }),
    // La confirmation est aussi notifiée dans l'application : la liste des notifications est rechargée.
    invalider: [[...CLES.rendezVous], ['citoyen', 'creneaux'], [...CLES.notifications]],
    apres: (r) => setConfirme(r),
  });
  const agenda = (agendas.data ?? []).find((a) => a.id === agendaId);
  if (confirme) {
    return (
      <div className={styles.pile}>
        <Bloc etiquette="Rendez-vous confirmé" titre={dateHeure(confirme.debut)}>
          <p className={styles.blocTexte}>
            {confirme.agenda.libelle} · {confirme.service.nom}
            {confirme.agenda.lieu ? ` · ${confirme.agenda.lieu}` : ''}. Un rappel vous est envoyé la veille. Annulation possible jusqu&apos;à {agenda?.delai_annulation_heures ?? 24} heures avant.
          </p>
        </Bloc>
        <div className={styles.ligneBoutons}>
          <LienBouton href="/citoyen/rendez-vous" variante="principal">
            Mes rendez-vous
          </LienBouton>
        </div>
      </div>
    );
  }
  return (
    <div className={styles.pile}>
      {(collectivites.data ?? []).length > 1 ? (
        <Selection
          id="rdv-collectivite"
          libelle="Collectivité"
          options={(collectivites.data ?? []).map((c) => ({ valeur: c.code, libelle: c.nom }))}
          value={collectivite}
          onChange={(e) => {
            setCollectivite(e.target.value);
            setAgendaId('');
            setDebut(null);
          }}
        />
      ) : null}
      <EtatRequete chargement={agendas.isPending} erreur={agendas.error} onReessayer={() => void agendas.refetch()} fonction="Les services recevant sur rendez-vous" lignes={3}>
        {(agendas.data ?? []).length === 0 ? (
          <EtatVide titre="Aucun service ne reçoit encore sur rendez-vous" icone={CalendarDays}>
            <p>Cette collectivité n&apos;a pas encore ouvert d&apos;agenda en ligne.</p>
          </EtatVide>
        ) : (
          <Selection
            id="rdv-agenda"
            libelle="Service"
            optionVide="Choisir un service"
            options={(agendas.data ?? []).map((a) => ({ valeur: a.id, libelle: `${a.libelle}${a.lieu ? ` (${a.lieu})` : ''}` }))}
            value={agendaId}
            onChange={(e) => {
              setAgendaId(e.target.value);
              setDebut(null);
            }}
            aide={agenda?.description ?? (agenda ? `Créneaux de ${agenda.duree_creneau_min} minutes` : undefined)}
          />
        )}
      </EtatRequete>
      {agendaId ? <Creneaux code={collectivite} agendaId={agendaId} choisi={debut} onChoisir={setDebut} /> : null}
      {debut ? (
        <>
          <p role="status">
            Créneau choisi : <strong>{dateHeure(debut)}</strong>
          </p>
          <ZoneTexte id="rdv-objet" libelle="Objet du rendez-vous" value={objet} onChange={(e) => setObjet(e.target.value)} facultatif rows={2} maxLength={500} />
          {reservation.messageErreur ? <Bandeau type="erreur">{reservation.messageErreur}</Bandeau> : null}
          <Bouton pleineLargeur chargement={reservation.enCours} libelleChargement="Réservation en cours" onClick={() => reservation.lancer(undefined)}>
            Confirmer le rendez-vous
          </Bouton>
        </>
      ) : null}
    </div>
  );
}

export function PageNouveauRendezVous() {
  const [choisie] = useCollectiviteChoisie();
  if (!choisie) return <Navigate to="/citoyen" replace />;
  return (
    <>
      <EnTeteCitoyen titre="Prendre rendez-vous" sousTitre="Choisissez le service, puis un créneau." retour="/citoyen/rendez-vous" libelleRetour="Rendez-vous" />
      <ExigeConnexion titre="Connectez-vous pour prendre rendez-vous">
        <Prise code={choisie.code} />
      </ExigeConnexion>
    </>
  );
}
